/**
 * Thin wrapper around video.js: creates a player for one source, reports a
 * small set of semantic events upward, and cleans up completely on dispose.
 *
 * Nothing in here decides WHEN to create/destroy/retry — that's the
 * controller's job. This module never touches global state and never patches
 * XMLHttpRequest: playlist/segment failures are observed through the
 * documented `videojs.Vhs.xhr.onResponse` hook.
 */

import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'
import 'videojs-contrib-quality-levels'
import 'videojs-mux'
import chromecast from '@silvermine/videojs-chromecast'

import type { PlayerConfig } from './config'
import { getViewerId } from './ids'
import { mountSettingsMenu, type SettingsMenuHandle } from './settings-menu'
import { mountContextMenu, type ContextMenuHandle } from './context-menu'
import { mountCastControls, type CastHandle } from './cast'
import { openShareOverlay } from './share-overlay'
import { mountTooltips, type TooltipsHandle } from './tooltips'
import { mountTopControls, type TopControlsHandle } from './top-controls'

export interface PlayerEvents {
  /** Playback actually started/resumed rendering frames */
  onPlaying: () => void
  /** The viewer (not our code) paused the player */
  onUserPause: () => void
  /** Fatal player error (media error, all sources failed) */
  onError: (message: string) => void
  /** A playlist/segment request returned >= 400 */
  onHttpError: (url: string, status: number) => void
  /** Autoplay was blocked even after falling back to muted */
  onAutoplayBlocked: () => void
  /** An AirPlay/Chromecast session ended — playback must return to this device */
  onRemoteSessionEnded: () => void
}

export interface PlayerHandle {
  readonly player: Player
  /** Resolves once video.js will actually accept a play() (bounded) */
  whenPlayable: () => Promise<void>
  /** Ignore the click currently in flight (see the preview handoff) */
  suppressNextClick: () => void
  /** True while a pause() we initiated is in flight (not a user pause) */
  isProgrammaticPause: () => boolean
  pause: () => void
  play: () => Promise<void>
  /** Jump to the live edge (no-op for VOD) */
  seekToLiveEdge: () => void
  /** Live progress probe used by the watchdog */
  probe: () => { currentTime: number; paused: boolean; readyState: number }
  dispose: () => void
}

/** Upper bound on waiting for a rebuilt player's source before trying play() anyway. */
const PLAYABLE_WAIT_MS = 4000

/** How long the post-scrub suppression lingers if playback doesn't resume. */
const SCRUB_RELEASE_MS = 400

/** Window in which a second click still counts as a double-click. */
const DOUBLE_CLICK_MS = 260

/** How long a click stays "already handled" after a preview handoff. */
const SUPPRESS_CLICK_MS = 600

let chromecastRegistered = false
let castSdkReady = false
let vhsHookInstalled = false
let activeHttpErrorSink: ((url: string, status: number) => void) | null = null
let activeMediaToken: { token: string; host: string } | null = null

/**
 * The VHS response hook is global and can only be registered, so we install it
 * once and route through a swappable sink bound to the active player.
 */
/**
 * Re-attach the CDN token to every media request.
 *
 * The token watch-guard mints is scoped by an ACL wildcard covering the whole
 * stream directory, so ONE token authorises the master playlist, every variant,
 * every segment, the DVR playlists and the DASH manifest alike. But only the
 * master URL we are handed carries it: the origin writes child URIs relative and
 * bare, and the edge strips its own token before the request reaches the origin,
 * so nothing downstream can put it back. Without this the master loads and the
 * very next request 403s.
 *
 * Kept as a URL rewrite rather than a header because it is what a cast receiver
 * would also need, and because a query parameter survives the relative-URI
 * resolution VHS does internally.
 */
function tokenFor(url: string): string | null {
  if (!activeMediaToken) return null
  // Same-origin-as-the-source check: never leak a bearer credential to a host
  // the grant was not issued for (ad tags, analytics beacons, poster images).
  try {
    if (new URL(url, location.href).host !== activeMediaToken.host) return null
  } catch {
    return null
  }
  return activeMediaToken.token
}

function withToken(url: string): string {
  const token = tokenFor(url)
  if (!token) return url
  if (/[?&]hdnts=/.test(url)) return url // already carries one
  return url + (url.includes('?') ? '&' : '?') + `hdnts=${token}`
}

/**
 * True when playback runs through VHS, where our onRequest hook attaches the
 * CDN token to every fetch. False only for a client that plays HLS natively
 * and so follows child URIs itself, appending nothing.
 *
 * THIS IS NOT "does the browser support native HLS". That was the old test and
 * it was wrong. VHS decides between MSE and native with
 *
 *   canUseMsePlayback = !supportsTypeNatively || overrideNative
 *   overrideNative defaults to !(IS_ANY_SAFARI || IS_IOS)
 *
 * so every non-Safari browser goes through MSE whatever canPlayType claims —
 * and Chrome does claim it. On a Chrome that answers "maybe" to
 * application/x-mpegURL, the old test concluded native, and the DVR window was
 * silently withheld from a client that could have signed for it perfectly
 * well. Mirroring VHS's real condition is the whole fix.
 *
 * The one case that genuinely cannot be signed is Safari or iOS playing HLS
 * natively, and overrideNative is deliberately left at its default there:
 * forcing MSE on Safari costs hardware decoding and interferes with AirPlay.
 */
export function canSignOwnRequests(): boolean {
  try {
    const nativeSafari = !!(videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS)
    if (!nativeSafari) return true // VHS overrides native: MSE, and we sign it
    const el = document.createElement('video')
    const native =
      !!el.canPlayType('application/x-mpegURL') || !!el.canPlayType('application/vnd.apple.mpegurl')
    return !native
  } catch {
    return false
  }
}

function installVhsResponseHook(): void {
  if (vhsHookInstalled) return
  vhsHookInstalled = true

  interface VhsXhr {
    onRequest?: (cb: (options: { uri?: string }) => { uri?: string }) => void
    onResponse?: (cb: (req: { uri?: string }, err: unknown, res: { statusCode?: number }) => void) => void
  }
  const vhsXhr = (videojs as unknown as { Vhs?: { xhr?: VhsXhr } }).Vhs?.xhr

  vhsXhr?.onRequest?.((options) => {
    if (options?.uri) options.uri = withToken(options.uri)
    return options
  })

  vhsXhr?.onResponse?.((request, _error, response) => {
    const status = response?.statusCode ?? 0
    if (status >= 400 && activeHttpErrorSink) {
      activeHttpErrorSink(request?.uri ?? '', status)
    }
  })
}

/**
 * Remember the token from the URL we were given, so child requests can reuse it.
 * Renewal replaces the source, which lands here again with the fresh token.
 */
export function setMediaToken(hlsUrl: string): void {
  try {
    const u = new URL(hlsUrl, location.href)
    const token = u.searchParams.get('hdnts')
    activeMediaToken = token ? { token, host: u.host } : null
  } catch {
    activeMediaToken = null
  }
}

/** Shareable URL: explicit config wins; iframed embeds share the host page
 *  (document.referrer); top-level playback shares its own URL. */
function shareUrlFor(config: PlayerConfig): string {
  if (config.shareUrl) return config.shareUrl
  if (window.self !== window.top && document.referrer) return document.referrer
  return window.location.href
}

/** Infer MIME from the URL path (v1 computed this before the URL existed — always undefined). */
function mimeFor(url: string): string {
  const path = url.split('?')[0]
  if (path.endsWith('.mpd')) return 'application/dash+xml'
  if (path.endsWith('.mp4')) return 'video/mp4'
  if (path.endsWith('.webm')) return 'video/webm'
  return 'application/x-mpegURL'
}

/**
 * video.js queues a `play()` that arrives before the source is ready onto the
 * next `ready`/`loadstart`. On a *rebuilt* player (source switch, or returning
 * from a cast session) both of those fire while `changingSrc_` is still true,
 * so the queued call re-queues itself for events that will never fire again —
 * the returned promise never settles and playback silently never starts.
 *
 * So we wait for the source ourselves and only call play() once the player is
 * genuinely ready. Resolves early (bounded) rather than blocking forever.
 */
function whenPlayable(player: Player): Promise<void> {
  const internals = player as unknown as { isReady_?: boolean; changingSrc_?: boolean }
  const isPlayable = (): boolean => Boolean(internals.isReady_) && !internals.changingSrc_
  if (isPlayable()) return Promise.resolve()

  const EVENTS = ['ready', 'loadstart', 'loadedmetadata', 'loadeddata', 'canplay']
  return new Promise<void>((resolve) => {
    let settled = false
    const finish = (): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      try {
        player.off(EVENTS, check)
      } catch {
        /* player already disposed */
      }
      resolve()
    }
    const check = (): void => {
      if (isPlayable()) finish()
    }
    const timer = setTimeout(finish, PLAYABLE_WAIT_MS)
    player.on(EVENTS, check)
    check()
  })
}

function createVideoElement(container: HTMLElement, poster: string | null): HTMLVideoElement {
  // Only ever one player element in the DOM.
  container.querySelectorAll('video-js, video.video-js, .video-js').forEach((el) => el.remove())

  const video = document.createElement('video')
  video.id = 'livepush-video'
  video.className = 'video-js vjs-theme-city'
  video.setAttribute('playsinline', '')
  video.controls = true
  video.preload = 'auto'
  if (poster) video.poster = poster
  container.appendChild(video)
  return video
}

export interface ChatBridge {
  isAvailable: () => boolean
  isOpen: () => boolean
  toggle: () => void
}

export interface CreatePlayerOptions {
  container: HTMLElement
  config: PlayerConfig
  hlsUrl: string
  sessionId: string | null
  muted: boolean
  events: PlayerEvents
  /** Late-bound view of the chat panel, for the right-click menu. */
  chat?: ChatBridge
}

/**
 * The Chromecast tech sits ahead of html5 in techOrder, so registering it when
 * the Cast SDK never loaded is not a missing button — it is a dead player. The
 * tech accepts the source, then throws reaching for a session that does not
 * exist ("cannot read properties of undefined (reading 'setVolume')"), and the
 * viewer gets MEDIA_ERR_SRC_NOT_SUPPORTED on a perfectly good stream.
 *
 * gstatic.com is blocked often enough — ad blockers, corporate proxies, China —
 * that this is a real population, not an edge case. No SDK, no cast tech; the
 * viewer loses the cast button and keeps their video.
 */
function castSdkPresent(): boolean {
  if (castSdkReady) return true
  const cast = (window as unknown as { chrome?: { cast?: { isAvailable?: boolean } } }).chrome?.cast
  return !!cast && cast.isAvailable !== false
}

/**
 * cast_sender.js is deferred, so on a fast authorize it can still be in flight
 * when the player mounts. The SDK announces itself through this global; we take
 * the callback at module load so the flag is set the moment it lands, and let
 * whoever cares know a rebuild would now gain them a cast button.
 */
interface CastCapableWindow extends Window {
  __onGCastApiAvailable?: (available: boolean) => void
}

const castReadyListeners = new Set<() => void>()

export function onCastSdkReady(listener: () => void): () => void {
  if (castSdkReady) {
    listener()
    return () => undefined
  }
  castReadyListeners.add(listener)
  return () => castReadyListeners.delete(listener)
}

/**
 * Wait, briefly, for the deferred Cast SDK.
 *
 * cast_sender.js is deferred and then initialises asynchronously, so on a fast
 * authorize the player can mount before `chrome.cast` exists — and a player
 * built without the cast tech has no cast button for the rest of its life.
 * Boot is already waiting on a network round trip, so the SDK almost always
 * lands inside that window; this just makes sure we do not race it.
 *
 * Bounded, because a viewer whose network blocks gstatic must not be made to
 * wait for a button they are never going to get.
 */
export function whenCastSdkReady(timeoutMs = 2000): Promise<boolean> {
  if (castSdkPresent()) return Promise.resolve(true)
  return new Promise((resolve) => {
    let done = false
    const finish = (ready: boolean) => {
      if (done) return
      done = true
      clearTimeout(timer)
      stop()
      resolve(ready)
    }
    const timer = setTimeout(() => finish(false), timeoutMs)
    const stop = onCastSdkReady(() => finish(true))
  })
}

;(() => {
  const w = window as CastCapableWindow
  const previous = w.__onGCastApiAvailable
  w.__onGCastApiAvailable = (available: boolean) => {
    previous?.(available)
    if (!available) return
    castSdkReady = true
    for (const listener of castReadyListeners) listener()
    castReadyListeners.clear()
  }
})()

/**
 * Make fullscreen apply to the STAGE rather than the player element.
 *
 * video.js fullscreens `player.el()`. Anything that is a sibling of the player
 * — the chat panel — is outside that element and simply vanishes when the
 * viewer goes fullscreen. Redirecting the request to the container puts both
 * columns on screen together, and it is a no-op for an embed without chat,
 * because the player fills the container anyway.
 *
 * Only element fullscreen can do this. iPhone Safari implements fullscreen on
 * the video element alone (webkitEnterFullscreen), so there we leave video.js's
 * own path in place and the chat closes for the duration: a broken button
 * would be a worse trade than a missing panel.
 *
 * Returns a teardown for the document listener.
 */
function makeFullscreenUseStage(player: Player, stage: HTMLElement): () => void {
  if (typeof stage.requestFullscreen !== 'function' || typeof document.exitFullscreen !== 'function') {
    return () => undefined
  }

  const onChange = (): void => {
    // Keep video.js's own state truthful: the control-bar icon, the
    // vjs-fullscreen class and every internal that reads isFullscreen() all
    // hang off this, and Esc never routes through our override.
    try {
      player.isFullscreen(document.fullscreenElement === stage)
    } catch {
      /* player disposed mid-transition */
    }
  }
  document.addEventListener('fullscreenchange', onChange)

  player.requestFullscreen = () => stage.requestFullscreen()
  player.exitFullscreen = () => document.exitFullscreen()

  return () => {
    document.removeEventListener('fullscreenchange', onChange)
    if (document.fullscreenElement === stage) void document.exitFullscreen().catch(() => undefined)
  }
}

export function createPlayer(opts: CreatePlayerOptions): PlayerHandle {
  const { container, config, hlsUrl, muted, events } = opts

  installVhsResponseHook()
  setMediaToken(hlsUrl)
  if (!config.audioOnly && !chromecastRegistered && castSdkPresent()) {
    try {
      chromecast(videojs, { preloadWebComponents: true })
      chromecastRegistered = true
    } catch {
      // Cast framework unavailable — non-fatal.
    }
  }

  const videoEl = createVideoElement(container, config.posterUrl)

  /**
   * Click-to-pause vs double-click-to-fullscreen.
   *
   * video.js runs both handlers, so a double-click used to pause the video,
   * go fullscreen, then play again — the first click's pause landing before
   * the browser could know a second was coming. Hold the play/pause toggle
   * for one double-click window and drop it if a second click arrives.
   */
  let clickToggleTimer: ReturnType<typeof setTimeout> | null = null
  /** Set when a click has already been acted on elsewhere (preview handoff) */
  let suppressClickUntil = 0

  const cancelPendingClick = (): void => {
    if (clickToggleTimer === null) return
    clearTimeout(clickToggleTimer)
    clickToggleTimer = null
  }

  const options: Record<string, unknown> = {
    controls: true,
    userActions: {
      click: () => {
        // Second click of a double-click: leave the pending toggle alone —
        // the double-click handler is about to cancel it.
        if (clickToggleTimer !== null || disposed) return
        // This click already did something (it took the player back from a
        // background preview); toggling on top of it would undo that.
        if (Date.now() < suppressClickUntil) return
        clickToggleTimer = setTimeout(() => {
          clickToggleTimer = null
          if (disposed) return
          if (player.paused()) {
            void player.play()
          } else {
            player.pause()
          }
        }, DOUBLE_CLICK_MS)
      },
      doubleClick: () => {
        cancelPendingClick()
        if (disposed) return
        // Same targets video.js would pick, minus the play/pause side effect.
        if (player.isInPictureInPicture() && !document.pictureInPictureElement) {
          void player.exitPictureInPicture()
        } else if (player.isFullscreen()) {
          void player.exitFullscreen()
        } else {
          void player.requestFullscreen()
        }
      },
    },
    // SVG icon set instead of the icon font: every glyph is then a real
    // element we can size, so the built-in controls match our own SVG buttons
    // (gear, AirPlay, cast) exactly instead of each rendering at its own
    // font-dependent optical size.
    experimentalSvgIcons: true,
    autoplay: false, // play() is called explicitly so failures are observable
    muted,
    preload: 'auto',
    liveui: true,
    bigPlayButton: true,
    // video.js's own error modal is raw plumbing shown to a viewer: "the media
    // could not be loaded, either because the server or network failed or
    // because the format is not supported" tells them nothing they can use, and
    // it renders on top of a spinner that keeps spinning. The controller owns
    // this surface instead.
    errorDisplay: false,
    controlBar: {
      // ±10s skip, live-aware (video.js clamps them to the seekable range).
      // Reel only: it is the theme built around a DVR window, and the one
      // whose second row has a centred transport to flank. The other themes
      // keep their single-row bar as it is.
      ...(config.theme === 'reel' && config.liveRewind
        ? { skipButtons: { forward: 10, backward: 10 } }
        : {}),
      timeDivider: true,
      currentTimeDisplay: true,
      durationDisplay: true,
      remainingTimeDisplay: false,
      playToggle: !config.disablePauseControl,
      seekToLive: true,
      liveDisplay: true,
      volumePanel: {},
      fullscreenToggle: true,
      playbackRateMenuButton: false,
    },
    html5: {
      vhs: { experimentalBufferBasedABR: true, useDevicePixelRatio: true },
      nativeAudioTracks: false,
      nativeVideoTracks: false,
      useBandwidthFromLocalStorage: true,
    },
    techOrder: config.audioOnly || !chromecastRegistered ? ['html5'] : ['chromecast', 'html5'],
    sources: [{ src: hlsUrl, type: mimeFor(hlsUrl) }],
    plugins: {
      ...(chromecastRegistered && !config.audioOnly
        ? {
            chromecast: {
              addButtonToControlBar: true,
              // Sit with the other remote/utility icons, before the gear.
              buttonPositionIndex: -3,
              requestTitleFn: () => config.title || config.embedId,
              requestSubtitleFn: () => (config.description || '').slice(0, 120),
            },
          }
        : {}),
      mux: {
        debug: false,
        data: {
          env_key: config.muxEnvKey,
          player_name: 'Live Player v2',
          player_init_time: Date.now(),
          viewer_user_id: getViewerId(),
          video_id: config.embedId,
          // Always the embed id, never the display title: titles change as
          // broadcasters reorganise their events, and analytics (views-api,
          // legacy player.livepush.io data) key on a constant per-embed value.
          video_title: config.embedId,
          video_stream_type: 'live',
          ...(config.nsid ? { ns: config.nsid, video_series: config.nsid } : {}),
        },
      },
    },
  }

  const player = videojs(videoEl, options)

  const releaseFullscreen = makeFullscreenUseStage(player, container)

  let disposed = false
  let programmaticPause = false

  activeHttpErrorSink = events.onHttpError

  player.on('playing', () => events.onPlaying())

  // Once ANY frame is decoded, drop the poster and show the real frame.
  // (The controller's prime-and-pause preview produces this frame for live
  // streams — VHS never downloads segments for a live source before play.)
  const markFrame = () => {
    if (!disposed) player.addClass('lp-has-frame')
  }
  player.one('loadeddata', markFrame)
  player.one('seeked', markFrame)

  /** Jump to the live edge (no-op for VOD). Used when resuming after preview. */
  const seekToLiveEdge = (): void => {
    try {
      const lt = (player as unknown as { liveTracker?: { isLive?: () => boolean; seekToLiveEdge?: () => void } })
        .liveTracker
      if (lt?.isLive?.()) lt.seekToLiveEdge?.()
    } catch {
      /* non-fatal */
    }
  }

  /**
   * video.js pauses playback on seek-bar mousedown and resumes on release, so
   * every scrub emits a real `pause`/`play` pair. That is NOT a viewer pause:
   * left alone it flashed the Netflix-style pause overlay, the big play button
   * and the starting spinner on every seek. `lp-scrubbing` marks the window so
   * both this module and the controller can ignore it.
   */
  let scrubbing = false
  let scrubEndTimer: ReturnType<typeof setTimeout> | null = null

  const startScrub = (): void => {
    if (disposed || scrubbing) return
    scrubbing = true
    if (scrubEndTimer) {
      clearTimeout(scrubEndTimer)
      scrubEndTimer = null
    }
    player.addClass('lp-scrubbing')
  }

  const endScrub = (): void => {
    if (!scrubbing || scrubEndTimer) return
    // video.js restarts playback asynchronously after the release — hold the
    // suppression until frames are moving again, or briefly if they aren't
    // (the viewer was already paused before scrubbing).
    const clear = (): void => {
      scrubEndTimer = null
      scrubbing = false
      if (!disposed) {
        player.off('playing', clear)
        player.removeClass('lp-scrubbing')
      }
    }
    player.one('playing', clear)
    scrubEndTimer = setTimeout(clear, SCRUB_RELEASE_MS)
  }

  player.ready(() => {
    if (disposed) return
    const progress = (player.el() as HTMLElement).querySelector('.vjs-progress-control')
    progress?.addEventListener('mousedown', startScrub, true)
    progress?.addEventListener('touchstart', startScrub, true)
  })
  document.addEventListener('mouseup', endScrub, true)
  document.addEventListener('touchend', endScrub, true)

  player.on('pause', () => {
    if (disposed) return
    if (programmaticPause) {
      programmaticPause = false
      return
    }
    // A seek-bar scrub pauses and resumes on its own — not a viewer pause.
    if (scrubbing) return
    // Real pauses mid-stream come from the viewer.
    if (!player.ended() && (player.currentTime() ?? 0) > 0) {
      events.onUserPause()
    }
  })

  player.on('error', () => {
    if (disposed) return
    const err = player.error()
    events.onError(err ? `${err.code}: ${err.message}` : 'unknown media error')
  })

  /*
    Tooltips for every icon on the stage: the control bar's and the chat
    panel's. Mounted on the stage rather than the player so one instance
    covers both, and mounted unconditionally — audio-only still has a bar.
    It strips the native `title` attributes as it goes, so this replaces the
    browser's tooltip rather than racing it.
  */
  const tooltips: TooltipsHandle = mountTooltips(container)

  // Settings menu: quality + playback speed (skip in audio-only).
  let settingsMenu: SettingsMenuHandle | null = null
  let contextMenu: ContextMenuHandle | null = null
  let castControls: CastHandle | null = null
  let topControls: TopControlsHandle | null = null
  if (!config.audioOnly) {
    player.ready(() => {
      if (disposed) return
      settingsMenu = mountSettingsMenu(player, {
        embedId: config.embedId,
        sessionId: opts.sessionId,
        shareUrl: shareUrlFor(config),
        title: config.title,
      })
      // Custom right-click menu, themed like the settings panel
      contextMenu = mountContextMenu(player, {
        embedId: config.embedId,
        sessionId: opts.sessionId,
        shareUrl: shareUrlFor(config),
        onShare: () =>
          openShareOverlay(player.el() as HTMLElement, {
            url: shareUrlFor(config),
            title: config.title,
          }),
        onTroubleshoot: () => settingsMenu?.openAt('feedback'),
        chat: opts.chat,
      })
      // AirPlay button + shared "playing on <device>" overlay
      castControls = mountCastControls(player, {
        onRemoteSessionEnded: () => events.onRemoteSessionEnded(),
      })
      // Mounted last: it moves buttons the two lines above just created.
      topControls = mountTopControls(player.el() as HTMLElement)
    })

  }

  const play = async (): Promise<void> => {
    try {
      await whenPlayable(player)
      if (disposed) return
      await player.play()
    } catch (err) {
      if (disposed) return
      if ((err as Error | undefined)?.name === 'NotAllowedError' && !player.muted()) {
        // Browser blocked audible autoplay — retry muted once.
        player.muted(true)
        try {
          await player.play()
          return
        } catch {
          /* fall through */
        }
      }
      events.onAutoplayBlocked()
    }
  }

  return {
    player,
    whenPlayable: () => whenPlayable(player),
    suppressNextClick: () => {
      suppressClickUntil = Date.now() + SUPPRESS_CLICK_MS
    },
    isProgrammaticPause: () => programmaticPause,
    pause: () => {
      programmaticPause = true
      try {
        player.pause()
      } catch {
        programmaticPause = false
      }
    },
    play,
    seekToLiveEdge,
    probe: () => ({
      currentTime: safeNumber(() => player.currentTime()),
      paused: safeBool(() => player.paused(), true),
      readyState: safeNumber(() => player.readyState()),
    }),
    dispose: () => {
      if (disposed) return
      disposed = true
      cancelPendingClick()
      if (scrubEndTimer) clearTimeout(scrubEndTimer)
      document.removeEventListener('mouseup', endScrub, true)
      document.removeEventListener('touchend', endScrub, true)
      settingsMenu?.dispose()
      contextMenu?.dispose()
      castControls?.dispose()
      topControls?.dispose()
      tooltips.dispose()
      releaseFullscreen()
      if (activeHttpErrorSink === events.onHttpError) activeHttpErrorSink = null
      const mux = (player as unknown as { mux?: { destroy?: () => void } }).mux
      try {
        mux?.destroy?.()
      } catch {
        /* non-fatal */
      }
      try {
        player.dispose()
      } catch {
        /* non-fatal */
      }
    },
  }
}

function safeNumber(fn: () => number | undefined): number {
  try {
    return fn() ?? 0
  } catch {
    return 0
  }
}

function safeBool(fn: () => boolean | undefined, fallback: boolean): boolean {
  try {
    return fn() ?? fallback
  } catch {
    return fallback
  }
}
